/**
 * Remote Debug Logger — when Corp admin enables debug mode,
 * this captures verbose step-by-step logs and sends them in batches.
 */

// Only activate when Corp API is explicitly configured — localhost:3010 fallback
// was causing 403 errors on remote LAN dev clients (#816)
const CORP_API = process.env.NEXT_PUBLIC_CORP_API_URL || ''
const MAX_ENTRIES = 200
const FLUSH_INTERVAL = 10_000
const POLL_INTERVAL = 60_000

interface DebugEntry {
  timestamp: string
  step: string
  component?: string
  context?: any
  url?: string
}

let debugActive = false
let queue: DebugEntry[] = []
let flushTimer: ReturnType<typeof setInterval> | null = null
let pollTimer: ReturnType<typeof setInterval> | null = null

function getSubscriberId(): string | null {
  try {
    const raw = localStorage.getItem('jm_user')
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed.subscriber_id || parsed.subscriberId || null
  } catch { return null }
}

export function debugLog(step: string, context?: any) {
  try {
    if (!debugActive) return
    const entry: DebugEntry = {
      timestamp: new Date().toISOString(),
      step,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      context,
    }
    queue.push(entry)
    if (queue.length > MAX_ENTRIES) queue.splice(0, queue.length - MAX_ENTRIES)
  } catch { /* never break the app */ }
}

export function flushDebugLogs() {
  try {
    if (queue.length === 0 || !CORP_API) return
    const batch = queue.splice(0, 20)
    fetch(`${CORP_API}/api/corp/issues/report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        appSource: 'web',
        appVersion: process.env.NEXT_PUBLIC_BUILD_ID || 'dev',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        url: typeof window !== 'undefined' ? window.location.href : '',
        errors: [{
          severity: 'info',
          category: 'debug_trace',
          message: `Debug trace batch (${batch.length} entries)`,
          context: { entries: batch },
        }],
      }),
    }).catch(() => {})
  } catch { /* swallow */ }
}

function startFlushing() {
  if (flushTimer) return
  flushTimer = setInterval(flushDebugLogs, FLUSH_INTERVAL)
}

function stopFlushing() {
  if (flushTimer) { clearInterval(flushTimer); flushTimer = null }
  flushDebugLogs() // final flush
}

async function checkDebugStatus(subscriberId: string) {
  try {
    if (!CORP_API) return
    const res = await fetch(`${CORP_API}/api/webhooks/debug-status/${subscriberId}`)
    if (!res.ok) return
    const data = await res.json()
    if (data.debugMode && !debugActive) {
      debugActive = true
      startFlushing()
    } else if (!data.debugMode && debugActive) {
      debugActive = false
      stopFlushing()
    }
  } catch { /* swallow */ }
}

export function startDebugPoller(subscriberId?: string) {
  try {
    if (typeof window === 'undefined') return
    const subId = subscriberId || getSubscriberId()
    if (!subId) return
    checkDebugStatus(subId)
    if (pollTimer) clearInterval(pollTimer)
    pollTimer = setInterval(() => checkDebugStatus(subId), POLL_INTERVAL)
  } catch { /* never break the app */ }
}
